import { geoPath, geoMercator } from "d3"
import { geoVoronoi } from "d3-geo-voronoi"
import * as shapeFrance from "../france-shape.json"
import Legend from "./Legend.js"
import { useMemo } from "react"

export default function MapVisualization({
  cities,
  french,
  log,
  tempHover,
  showCity,
  levelToColor,
  getCityWeek,
  getUniqueName,
  handleMouseOver,
  handleMouseOut,
  handleclick,
  idfCities,
  waterValues,
  endIndexDataviz,
}) {
  const styles = {
    width: 300,
    height: 350,
  }

  const generatedVoronoi = useMemo(() => {
    if (!cities || cities.length === 0) return { features: [] }

    const coords = cities.map((d) => [
      parseFloat(String(d.longitude || d.centroide?.lon).replace(",", ".")),
      parseFloat(String(d.latitude || d.centroide?.lat).replace(",", ".")),
    ])

    const v = geoVoronoi()(coords)
    const polys = v.polygons()

    polys.features.forEach((feature, i) => {
      feature.properties.site = {
        info: {
          ...cities[i],
          unique: getUniqueName(cities[i]),
        },
        geometry: {
          coordinates: coords[i],
        },
      }
    })

    return polys
  }, [cities, getUniqueName])

  const mercator = useMemo(() => {
    const geoJson = shapeFrance.default || shapeFrance
    return geoMercator().fitExtent(
      [
        [0, 0],
        [styles.width, styles.height],
      ],
      geoJson,
    )
  }, [styles.width, styles.height])

  const generatePath = (
    cellPolygon,
    index,
    projection,
    isMask = false,
    neon = false,
  ) => {
    const info = cellPolygon.properties.site.info
    const cityUnique = info.unique
    const selected = showCity && showCity.includes(cityUnique)
    const hover = tempHover && tempHover.includes(cityUnique)

    return (
      <path
        key={`${index}-${neon ? "neon" : "base"}`}
        fill={neon ? "transparent" : levelToColor(getCityWeek(cityUnique))}
        stroke="transparent"
        data-unique={cityUnique}
        d={geoPath().projection(projection)(cellPolygon.geometry)}
        className={`${selected ? "selected" : ""} ${hover ? "activated" : ""} ${neon ? "station-neon" : "station"}`}
        onMouseLeave={handleMouseOut}
        onMouseEnter={handleMouseOver}
        onClick={handleclick}
        mask={isMask ? `url(#mask-${cityUnique.replace(/\s+/g, "-")})` : ""}
      />
    )
  }

  const generateMask = (cellPolygon, index, projection) => {
    const cityUnique = cellPolygon.properties.site.info.unique
    return (
      <mask
        key={`mask-${index}`}
        id={`mask-${cityUnique.replace(/\s+/g, "-")}`}
      >
        <path fill="white" d={geoPath().projection(projection)(cellPolygon)} />
      </mask>
    )
  }

  const generateText = (cellPolygon, index, projection) => {
    const info = cellPolygon.properties.site.info
    const cityUnique = info.unique
    const coords = projection(cellPolygon.properties.site.geometry.coordinates)

    if (!coords) return null

    const valNow = getCityWeek(cityUnique)
    const valPrev = getCityWeek(cityUnique, Math.max(0, waterValues.length - 2))

    return (
      <text
        key={`text-${index}`}
        className={`france-svg-label ${tempHover.includes(cityUnique) ? "activated" : ""}`}
        x={coords[0]}
        y={coords[1]}
        onMouseLeave={handleMouseOut}
        onMouseEnter={handleMouseOver}
        onClick={handleclick}
      >
        <tspan>{valNow > valPrev ? "▲" : valNow < valPrev ? "▼" : ""}</tspan>
      </text>
    )
  }

  return (
    <div id="maps">
      <svg
        className="france-svg"
        viewBox={`0 0 ${styles.width} ${styles.height}`}
      >
        <defs>
          <filter id="neon" x="-100%" y="-100%" width="400%" height="400%">
            <feGaussianBlur stdDeviation="5" in="SourceGraphic" result="blur" />
            <feBlend in="SourceGraphic" in2="blur" mode="normal" />
          </filter>
          <clipPath id="clip">
            <path
              d={geoPath().projection(mercator)(
                shapeFrance.default || shapeFrance,
              )}
            />
          </clipPath>
          {generatedVoronoi.features.map((cell, i) =>
            generateMask(cell, i, mercator),
          )}
        </defs>

        <g clipPath="url(#clip)">
          <path
            d={geoPath().projection(mercator)(
              shapeFrance.default || shapeFrance,
            )}
            fill="#f0f0f0"
            stroke="#ccc"
          />
          {generatedVoronoi.features.map((cell, i) =>
            generatePath(cell, i, mercator, false, false),
          )}
          {generatedVoronoi.features.map((cell, i) =>
            generatePath(cell, i, mercator, true, true),
          )}
        </g>

        <g pointerEvents="none">
          {generatedVoronoi.features
            .filter(
              (cell) =>
                !idfCities.includes(
                  cell.properties.site.info.commune?.toLowerCase(),
                ),
            )
            .map((cell, i) => generateText(cell, i, mercator))}
        </g>
      </svg>

      {/* BRANCHEMENT CRUCIAL : log={log} */}
      <Legend log={log} french={french} levelToColor={levelToColor} />
    </div>
  )
}
